/**
 * The board's cards (v0.9.0; ADR 0189): in Logged - Open and Closed, one card
 * per ticket. The owner, 2026-10-21: "Logged - Open and Closed show one card
 * per ticket, with its total and a line per block. The popup lists its time
 * entries and has one Close ticket. Column counts are tickets. Autotask still
 * gets one time entry per block."
 *
 * Work added to an open ticket (ADR 0187) is logged as its own time entry, so
 * one ticket can have several of the day's logged blocks. Those blocks share
 * one card: its client, its ticket, their total and a line per block. Time
 * entered straight in Autotask on that ticket (ADR 0183) joins the card as a
 * line of its own. A ticket with one block and nothing else keeps that block's
 * own card, exactly as before; a logged block whose ticket wasn't read back
 * keeps its own; and a ticket with only time entered in Autotask keeps its
 * dashed cards.
 *
 * Pure functions of the day view's model, so the page only lays them out.
 */
import { formatDuration } from "../../lib/durations";
import type { Lane } from "../../lib/sources";
import type { BlockView, ColumnId, LoggedEntryView } from "./model";
import { type TicketView, ticketColumn } from "./tickets";

/** One of a ticket's time entries that day: a block logged here, or time entered straight in Autotask. */
export interface TicketLine {
  /** The block's ID, or the entry's (its activity's). */
  id: string;
  kind: "block" | "entry";
  startedAt: string;
  /** Null: Autotask has only a date and hours for it. */
  endedAt: string | null;
  seconds: number;
  duration: string;
  /** A block's sources, as its own card shows them; none for time entered in Autotask. */
  lanes: Lane[];
}

/** A ticket's one card, for its blocks and the time entered in Autotask on it that day. */
export interface TicketCardView {
  /** Its key on the board: `ticket:` and its Autotask ID. */
  key: string;
  /** Its first block's ticket: every block on it has the same. */
  ticket: TicketView;
  column: ColumnId;
  /** Its client's name, as its blocks' cards say it. */
  title: string;
  clientId: string | null;
  isHipaa: boolean;
  /** Its blocks, in time order: the card opens the first's popup. */
  blocks: BlockView[];
  /** Every line, blocks and time entered in Autotask, in time order. */
  lines: TicketLine[];
  /** The lines' total. */
  seconds: number;
  duration: string;
  /** Its first line's start: where it sits in its column. */
  startedAt: string;
}

/** A card on the board: a block's, a dashed card of time entered in Autotask, or a ticket's. */
export type BoardCard =
  | { kind: "block"; block: BlockView }
  | { kind: "entry"; entry: LoggedEntryView }
  | { kind: "ticket"; ticket: TicketCardView };

function byStart(a: { startedAt: string; id: string }, b: { startedAt: string; id: string }): number {
  return Date.parse(a.startedAt) - Date.parse(b.startedAt) || a.id.localeCompare(b.id);
}

function blockLine(block: BlockView): TicketLine {
  return {
    id: block.id,
    kind: "block",
    startedAt: block.startedAt,
    endedAt: block.endedAt,
    seconds: block.seconds,
    duration: block.duration,
    lanes: block.lanes,
  };
}

function entryLine(entry: LoggedEntryView): TicketLine {
  return {
    id: entry.id,
    kind: "entry",
    startedAt: entry.startedAt,
    endedAt: entry.endedAt,
    seconds: entry.seconds,
    duration: entry.duration,
    lanes: [],
  };
}

interface Group {
  ticket: TicketView;
  blocks: BlockView[];
  entries: LoggedEntryView[];
}

function ticketCard(group: Group): TicketCardView {
  const blocks = [...group.blocks].sort(byStart);
  const lines = [...blocks.map(blockLine), ...group.entries.map(entryLine)].sort(
    // A block before time entered in Autotask that starts with it.
    (a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt) || (a.kind === b.kind ? 0 : a.kind === "block" ? -1 : 1) || a.id.localeCompare(b.id),
  );
  const [first] = blocks;
  const seconds = lines.reduce((sum, line) => sum + line.seconds, 0);
  return {
    key: `ticket:${String(group.ticket.ticketId)}`,
    ticket: first?.ticket ?? group.ticket,
    column: first?.column ?? ticketColumn(group.ticket.state),
    title: first?.title ?? "",
    clientId: first?.clientId ?? null,
    isHipaa: blocks.some((b) => b.isHipaa),
    blocks,
    lines,
    seconds,
    duration: formatDuration(seconds),
    startedAt: lines[0]?.startedAt ?? first?.startedAt ?? "",
  };
}

function startOf(card: BoardCard): number {
  switch (card.kind) {
    case "block":
      return Date.parse(card.block.startedAt);
    case "entry":
      return Date.parse(card.entry.startedAt);
    case "ticket":
      return Date.parse(card.ticket.startedAt);
  }
}

/**
 * The board's cards, in start order (ADR 0189): a ticket's card for each
 * ticket with more than one time entry on the day (two logged blocks, or one
 * and time entered in Autotask), and each other block and dashed card as
 * before. Only logged blocks share a card, and only by their ticket's
 * Autotask ID, never by its number or a name.
 */
export function boardCards(blocks: readonly BlockView[], entries: readonly LoggedEntryView[]): BoardCard[] {
  const groups = new Map<number, Group>();
  for (const block of blocks) {
    if (block.kind === "logged" && block.ticket !== null) {
      const group = groups.get(block.ticket.ticketId) ?? { ticket: block.ticket, blocks: [], entries: [] };
      group.blocks.push(block);
      groups.set(block.ticket.ticketId, group);
    }
  }
  for (const entry of entries) {
    if (entry.ticket !== null) {
      groups.get(entry.ticket.ticketId)?.entries.push(entry);
    }
  }
  const shared = (ticket: TicketView | null): Group | undefined => {
    const group = ticket === null ? undefined : groups.get(ticket.ticketId);
    return group !== undefined && group.blocks.length + group.entries.length > 1 ? group : undefined;
  };

  const cards: BoardCard[] = [];
  const placed = new Set<Group>();
  for (const block of blocks) {
    const group = block.kind === "logged" ? shared(block.ticket) : undefined;
    if (group === undefined) {
      cards.push({ kind: "block", block });
    } else if (!placed.has(group)) {
      placed.add(group);
      cards.push({ kind: "ticket", ticket: ticketCard(group) });
    }
  }
  for (const entry of entries) {
    if (shared(entry.ticket) === undefined) {
      cards.push({ kind: "entry", entry });
    }
  }
  // Stable: a block's card before a dashed card that starts with it, as before.
  return cards.sort((a, b) => startOf(a) - startOf(b));
}

/** The column a card sits in. */
export function cardColumn(card: BoardCard): ColumnId | null {
  switch (card.kind) {
    case "block":
      return card.block.column;
    case "entry":
      return card.entry.column;
    case "ticket":
      return card.ticket.column;
  }
}

/** The ticket's card a block shares with its ticket's other time entries that day; null when it has its own. */
export function ticketCardOf(cards: readonly BoardCard[], blockId: string): TicketCardView | null {
  for (const card of cards) {
    if (card.kind === "ticket" && card.ticket.blocks.some((b) => b.id === blockId)) {
      return card.ticket;
    }
  }
  return null;
}
